/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import ReactMarkdown from 'react-markdown';
import { Bug, FileText, Plus, Tag, Calendar, User, Clock, Link as LinkIcon, MapPin, Loader, Wrench, Code, Copy, Check, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Issue, IssueType, IssueStatus } from '@shared/utils/issues';
import { IssueDetails } from './IssueDetails';

interface IssueCardProps {
  issue: Issue;
  isExpanded: boolean;
  isGrooming: boolean;
  isGeneratingCode: boolean;
  isEditing: boolean;
  isUpdating: boolean;
  editedFields: Partial<Issue>;
  statusOptions: IssueStatus[];
  canGenerateCode: boolean;
  getCodeActionLabel: (type: IssueType) => string;
  getStatusColor: (status: IssueStatus) => string;
  onToggleExpansion: () => void;
  onGroomIssue: () => void;
  onGenerateCode: () => void;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSaveEdits: () => void;
  onFieldChange: (field: keyof Issue, value: any) => void;
  onStatusUpdate: (newStatus: IssueStatus) => void;
  onAddLink: () => void;
  onDeleteLink: (linkIndex: number) => void;
  onRequestDelete: () => void;
}

export function IssueCard({
  issue,
  isExpanded,
  isGrooming,
  isGeneratingCode,
  isEditing,
  isUpdating,
  editedFields,
  statusOptions,
  canGenerateCode,
  getCodeActionLabel,
  getStatusColor,
  onToggleExpansion,
  onGroomIssue,
  onGenerateCode,
  onStartEditing,
  onCancelEditing,
  onSaveEdits,
  onFieldChange,
  onStatusUpdate,
  onAddLink,
  onDeleteLink,
  onRequestDelete
}: IssueCardProps) {
  const [copied, setCopied] = useState(false);

  const getTypeIcon = (type: IssueType) => {
    switch (type) {
      case 'Bug': return <Bug size={16} style={{ color: '#dc3545' }} />;
      case 'Task': return <FileText size={16} style={{ color: '#6c757d' }} />;
      case 'Feature': return <Plus size={16} style={{ color: '#28a745' }} />;
      case 'Release': return <Tag size={16} style={{ color: '#007bff' }} />;
      default: return <FileText size={16} />;
    }
  };

  /**
   * Format issue for use with coding agents (Cursor, Claude Code, etc.)
   * Output format: <description>\n\nContext: @file1 @file2 @file3
   */
  const formatIssueForAgent = (): string => {
    let output = issue.description.trim();
    
    // Add file references from links with @ prefix
    if (issue.links && issue.links.length > 0) {
      const fileRefs = issue.links.map(link => `@${link}`).join(' ');
      output += `\n\nContext: ${fileRefs}`;
    }
    
    return output;
  };

  const handleCopyForAgent = async () => {
    const text = formatIssueForAgent();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy issue:', err);
    }
  };

  // Extract the issue ID for use as DOM id (e.g., "BUG-001" from "BUG-001: Title")
  const issueId = issue.id.split(':')[0].trim();

  return (
    <div
      id={issueId}
      style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '16px',
        background: 'white',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, background-color 0.5s'
      }}
      onClick={(e) => {
        // Don't expand if clicking on buttons
        if ((e.target as HTMLElement).closest('button')) {
          return;
        }
        onToggleExpansion();
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {getTypeIcon(issue.type)}
          <h4 style={{ margin: 0, color: '#333' }}>{issue.id}</h4>
          <span
            style={{
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              backgroundColor: getStatusColor(issue.status),
              color: 'white'
            }}
          >
            {issue.status}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <Calendar size={12} style={{ marginRight: '4px' }} />
            {new Date(issue.createdAt).toLocaleDateString()}
          </div>
          <button
            onClick={handleCopyForAgent}
            className="btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '6px',
              fontSize: '12px',
              backgroundColor: 'transparent',
              border: '1px solid #ddd',
              borderRadius: '4px',
              color: copied ? '#28a745' : '#666',
              minWidth: '32px'
            }}
            title="Copy issue for coding agent (Cursor, Claude Code, etc.)"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
          <button
            type="button"
            onClick={onRequestDelete}
            className="btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              fontSize: '12px',
              backgroundColor: 'transparent',
              border: '1px solid #dc3545',
              borderRadius: '4px',
              color: '#dc3545',
              justifyContent: 'center'
            }}
            title="Delete this issue from issues.md"
          >
            <Trash2 size={12} />
          </button>          
          <button
            onClick={onGroomIssue}
            disabled={isGrooming}
            className="btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              fontSize: '12px',
              backgroundColor: '#0066cc',
              borderColor: '#0066cc',
              minWidth: '80px',
              justifyContent: 'center'
            }}
            title="Analyze and improve this issue using AI grooming tools"
          >
            {isGrooming ? (
              <>
                <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
                Grooming
              </>
            ) : (
              <>
                <Wrench size={12} />
                Groom
              </>
            )}
          </button>
          {canGenerateCode && (
            <button
              onClick={onGenerateCode}
              disabled={isGeneratingCode}
              className="btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: '#28a745',
                borderColor: '#28a745',
                minWidth: '90px',
                justifyContent: 'center'
              }}
              title={`Generate code to ${issue.type === 'Bug' ? 'fix' : issue.type === 'Feature' ? 'build' : 'implement'} this ${issue.type.toLowerCase()} using AI`}
            >
              {isGeneratingCode ? (
                <>
                  <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
                  Generating
                </>
              ) : (
                <>
                  <Code size={12} />
                  {getCodeActionLabel(issue.type)}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <div style={{ margin: '0 0 12px 0', color: '#666' }}>
        <ReactMarkdown 
          components={{
            p: ({node, ...props}) => <p style={{ margin: '0 0 8px 0' }} {...props} />,
            ul: ({node, ...props}) => <ul style={{ margin: '0 0 8px 0', paddingLeft: '20px' }} {...props} />,
            ol: ({node, ...props}) => <ol style={{ margin: '0 0 8px 0', paddingLeft: '20px' }} {...props} />,
            h1: ({node, ...props}) => <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 'bold' }} {...props} />,
            h2: ({node, ...props}) => <h5 style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: 'bold' }} {...props} />,
            h3: ({node, ...props}) => <h6 style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: 'bold' }} {...props} />,
            strong: ({node, ...props}) => <strong style={{ fontWeight: 'bold' }} {...props} />,
            code: ({node, ...props}) => <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px', fontSize: '12px' }} {...props} />
          }}
        >
          {issue.description}
        </ReactMarkdown>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '14px', color: '#666' }}>
        {issue.assignedTo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <User size={14} />
            {issue.assignedTo}
          </div>
        )}
        
        {issue.estimate && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Clock size={14} />
            {issue.estimate}
          </div>
        )}
        
        {issue.effort !== undefined && issue.effort > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Clock size={14} />
            {issue.effort}h logged
          </div>
        )}
        
        {issue.version && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Tag size={14} />
            {issue.version}
          </div>
        )}
        
        {issue.plan && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <MapPin size={14} />
            Plan
          </div>
        )}
        
        {issue.links && issue.links.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <LinkIcon size={14} />
            {issue.links.length} link{issue.links.length > 1 ? 's' : ''}
          </div>
        )}
      </div>

      {issue.labels && issue.labels.length > 0 && (
        <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {issue.labels.map((label, labelIndex) => (
            <span
              key={labelIndex}
              style={{
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                backgroundColor: '#e9ecef',
                color: '#495057'
              }}
            >
              {label}
            </span>
          ))}
        </div>
      )}

      {/* Expanded view */}
      {isExpanded && (
        <IssueDetails
          issue={issue}
          isEditing={isEditing}
          isUpdating={isUpdating}
          editedFields={editedFields}
          statusOptions={statusOptions}
          getStatusColor={getStatusColor}
          onStartEditing={onStartEditing}
          onCancelEditing={onCancelEditing}
          onSaveEdits={onSaveEdits}
          onFieldChange={onFieldChange}
          onStatusUpdate={onStatusUpdate}
          onAddLink={onAddLink}
          onDeleteLink={onDeleteLink}
        />
      )}
    </div>
  );
}

